/**
 * ManagementReportPrintView.tsx
 *
 * A hidden-from-screen, print-only component that renders the full Management
 * Report PDF when triggered via react-to-print (useReactToPrint).
 *
 * Layout (landscape, 4 pages):
 *   Page 1 — Chart (ComposedChart: balance + income/expense bars)
 *            + Running-balance table
 *   Page 2 — Category matrix: סיווג תזרים  (Cash-Flow Classification)
 *   Page 3 — Category matrix: סיווג הנהלת חשבונות (Accounting Classification)
 *
 * Receives all pre-computed data as props — does NOT fetch anything itself,
 * so it never breaks the React component tree.
 */

import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  Area,
  Bar,
} from 'recharts';
import type { ForecastRow } from '../../lib/rolloverUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChartPoint {
  date:     string;
  fullDate: string;
  balance:  number;
  income:   number;
  expense:  number;
}

export interface PrintViewProps {
  /** ref forwarded from CashflowDashboard for react-to-print */
  companyName: string;
  startDate:   string;   // YYYY-MM-DD
  endDate:     string;   // YYYY-MM-DD
  /** Bucketised chart points (already bucketed at appropriate resolution) */
  chartData:   ChartPoint[];
  /** All unified rows for the selected period (for the list table) */
  periodRows:  Array<{
    effectiveDate:  string;
    description:    string;
    amount:         number;
    /** Pre-computed cumulative running balance AT this transaction (opening + all txns up to and including this row). */
    runningBalance: number;
  }>;
  /** ALL unified rows for the current year (for the category matrices) */
  allYearRows: ForecastRow[];
  /** Selected year (for matrix header) */
  selectedYear: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES_SHORT = ['ינו','פבר','מרץ','אפר','מאי','יונ','יול','אוג','ספט','אוק','נוב','דצמ'];

const CASHFLOW_LABELS: Record<string, string> = {
  current_business:       'שוטף – פעילות עסקית',
  financing_loan:         'מימון – הלוואה',
  intra_group_loan:       'הלוואה תוך-קבוצתית',
  investment:             'השקעה',
  savings:                'חיסכון',
  taxes:                  'מיסים',
  inter_account_transfer: 'העברות בין חשבונות',
  personal:               'אישי',
  other:                  'אחר',
};

const PALETTE = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#06b6d4','#f97316','#ec4899','#84cc16','#14b8a6',
  '#6366f1','#a3a3a3','#eab308','#0ea5e9','#d946ef','#22c55e',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Count calendar days between two YYYY-MM-DD strings (inclusive of both). */
function daysBetween(start: string, end: string): number {
  // Parse as noon UTC to avoid any DST / timezone offset artefacts.
  const ms = Date.parse(end + 'T12:00:00Z') - Date.parse(start + 'T12:00:00Z');
  return Math.round(ms / 86_400_000) + 1; // +1 → inclusive
}

const ILS = new Intl.NumberFormat('he-IL', {
  style: 'currency', currency: 'ILS',
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

const fmtDate = (d: string) => d.split('-').reverse().join('/');

// ─── Matrix helpers ───────────────────────────────────────────────────────────

interface MatrixRow {
  category:    string;
  months:      number[];   // [0..11] net
  total:       number;
  colorIndex:  number;
}

function buildMatrix(rows: ForecastRow[], mode: 'cashflow' | 'accounting'): {
  matrixRows:   MatrixRow[];
  columnTotals: number[];
  grandTotal:   number;
} {
  const monthOf = (d: string) => Math.max(0, Math.min(11, parseInt(d.slice(5, 7), 10) - 1));

  const labelOf = (r: ForecastRow): string => {
    if (mode === 'cashflow') {
      const key = r.cashflowMapping;
      if (!key) return 'לא מסווג';
      return CASHFLOW_LABELS[key as string] ?? String(key);
    }
    const n = r.accountingCategoryName;
    return n && n !== '—' ? n : 'לא מסווג';
  };

  const map = new Map<string, number[]>();
  for (const row of rows) {
    const cat = labelOf(row);
    const mi  = monthOf(row.effectiveDate);
    if (!map.has(cat)) map.set(cat, new Array<number>(12).fill(0));
    const bucket = map.get(cat)!;
    bucket[mi] = (bucket[mi] ?? 0) + row.amount;
  }

  const matrixRows: MatrixRow[] = [];
  for (const [category, months] of map) {
    matrixRows.push({
      category,
      months,
      total: months.reduce((s, v) => s + v, 0),
      colorIndex: 0,
    });
  }
  matrixRows.sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  matrixRows.forEach((r, i) => { r.colorIndex = i % PALETTE.length; });

  const columnTotals = new Array<number>(12).fill(0);
  for (const row of matrixRows) {
    row.months.forEach((v, i) => { columnTotals[i] = (columnTotals[i] ?? 0) + v; });
  }
  const grandTotal = columnTotals.reduce((s, v) => s + v, 0);

  return { matrixRows, columnTotals, grandTotal };
}

// ─── Print CSS (injected as a <style> tag inside the hidden component) ────────

const PRINT_STYLE = `
@media print {
  @page { size: landscape; margin: 10mm; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: 'Arial', sans-serif; direction: rtl; }
}
.print-page-break  { break-before: page; page-break-before: always; }
.print-avoid-break { break-inside: avoid; page-break-inside: avoid; }
.print-tr-nobreak  { break-inside: avoid; page-break-inside: avoid; }

/* Prevent any table row from splitting across a page break */
@media print {
  tr { break-inside: avoid; page-break-inside: avoid; }
  thead { display: table-header-group; }
}
`;

// ─── Mini matrix table ────────────────────────────────────────────────────────

function PrintMatrix({ rows, columnTotals, grandTotal, title }: {
  rows:         MatrixRow[];
  columnTotals: number[];
  grandTotal:   number;
  title:        string;
}) {
  const fmtCell = (v: number) => v === 0 ? '—' : ILS.format(v);
  const cls = (v: number) =>
    v > 0 ? { color: '#065f46', fontWeight: 600 } :
    v < 0 ? { color: '#991b1b', fontWeight: 600 } :
    { color: '#d1d5db' };

  return (
    <div>
      <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: 4 }}>
        {title}
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', width: 130, fontWeight: 700, color: '#374151' }}>קטגוריה</th>
              {MONTH_NAMES_SHORT.map((m, i) => (
                <th key={i} style={{ padding: '4px 3px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#6b7280', width: 52 }}>{m}</th>
              ))}
              <th style={{ padding: '4px 6px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 700, color: '#374151', background: '#f1f5f9', width: 68 }}>סה״כ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="print-tr-nobreak" style={{ background: ri % 2 === 0 ? '#fff' : '#f9fafb' }}>
                <td style={{ padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #f1f5f9', fontWeight: 600, fontSize: 8.5 }}>
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: PALETTE[row.colorIndex] ?? '#a3a3a3', marginLeft: 4, verticalAlign: 'middle' }} />
                  {row.category}
                </td>
                {row.months.map((v, mi) => (
                  <td key={mi} style={{ padding: '3px 3px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', ...cls(v) }}>
                    {fmtCell(v)}
                  </td>
                ))}
                <td style={{ padding: '3px 6px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', fontWeight: 700, background: '#f8fafc', ...cls(row.total) }}>
                  {ILS.format(row.total)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
              <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, fontSize: 9, color: '#374151' }}>סה״כ חודשי</td>
              {columnTotals.map((v, i) => (
                <td key={i} style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 700, ...cls(v) }}>
                  {fmtCell(v)}
                </td>
              ))}
              <td style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 800, background: '#e2e8f0', ...cls(grandTotal) }}>
                {ILS.format(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ─── Main print component (forwarded ref for react-to-print) ─────────────────

export const ManagementReportPrintView = React.forwardRef<HTMLDivElement, PrintViewProps>(
  function ManagementReportPrintView(
    { companyName, startDate, endDate, chartData, periodRows, allYearRows, selectedYear },
    ref,
  ) {
    // ── Date-range size determines which table to render ─────────────────────
    const dayCount = useMemo(
      () => daysBetween(startDate, endDate),
      [startDate, endDate],
    );
    const useMonthly = dayCount > 31;

    // ── Detail rows: one row per transaction (≤ 31 days) ─────────────────────
    // Use the pre-computed runningBalance passed in from CashflowDashboard so
    // the figure matches the chart and KPI cards exactly (correct opening balance).
    const detailRows = useMemo(() => {
      return periodRows.map(r => ({
        date:        fmtDate(r.effectiveDate),
        description: r.description,
        income:      r.amount > 0 ? r.amount : 0,
        expense:     r.amount < 0 ? r.amount : 0,
        balance:     r.runningBalance,
      }));
    }, [periodRows]);

    // Build both matrices from allYearRows
    const cfMatrix   = useMemo(() => buildMatrix(allYearRows, 'cashflow'),   [allYearRows]);
    const acctMatrix = useMemo(() => buildMatrix(allYearRows, 'accounting'), [allYearRows]);

    const fmtDisplayDate = (d: string) => d.split('-').reverse().join('/');

    return (
      <div
        ref={ref}
        dir="rtl"
        style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: 11,
          color: '#1e293b',
          background: '#fff',
          padding: 16,
          direction: 'rtl',
        }}
      >
        {/* ── Injected print CSS ── */}
        <style>{PRINT_STYLE}</style>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PAGE 1 — Report header + Chart + Running-balance table            */}
        {/* ══════════════════════════════════════════════════════════════════ */}

        {/* Report header */}
        <div style={{ borderBottom: '3px solid #2563eb', paddingBottom: 8, marginBottom: 14 }}>
          <h1 style={{ fontSize: 17, fontWeight: 800, color: '#1e40af', margin: 0 }}>
            דוח תזרים מזומנים
          </h1>
          <p style={{ fontSize: 12, color: '#475569', margin: '4px 0 0' }}>
            חברת <strong>{companyName}</strong>
            &nbsp;·&nbsp;
            תקופה: {fmtDisplayDate(startDate)} – {fmtDisplayDate(endDate)}
          </p>
        </div>

        {/* Chart */}
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
            תרשים תזרים מזומנים
          </h2>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={chartData} margin={{ top: 6, right: 8, left: 16, bottom: 4 }}>
              <defs>
                <linearGradient id="printBalGradPos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="printBalGradNeg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} interval="preserveStartEnd" />
              <YAxis tickFormatter={v => ILS.format(v as number)} tick={{ fontSize: 8, fill: '#6b7280' }} tickLine={false} axisLine={false} width={72} />
              <RechartsTooltip />
              <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="4 4" strokeWidth={1} />
              <Bar dataKey="income"  name="הכנסות" fill="#10b981" fillOpacity={0.7} maxBarSize={20} radius={[2,2,0,0]} />
              <Bar dataKey="expense" name="הוצאות" fill="#ef4444" fillOpacity={0.7} maxBarSize={20} radius={[0,0,2,2]} />
              <Area
                type="monotone"
                dataKey="balance"
                name="יתרה מצטברת"
                stroke="#2563eb"
                strokeWidth={2}
                fill={
                  (chartData[chartData.length - 1]?.balance ?? 0) >= 0
                    ? 'url(#printBalGradPos)'
                    : 'url(#printBalGradNeg)'
                }
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Chart legend */}
          <div style={{ display: 'flex', gap: 16, fontSize: 9, color: '#6b7280', marginTop: 4, justifyContent: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 20, height: 2, background: '#2563eb', borderRadius: 1 }} />
              יתרה מצטברת
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: '#10b981', borderRadius: 2, opacity: 0.8 }} />
              הכנסות
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: '#ef4444', borderRadius: 2, opacity: 0.8 }} />
              הוצאות
            </span>
          </div>
        </div>

        {/* ── Section 2: Transaction table — monthly summary OR detail list ── */}
        <div className="print-page-break print-avoid-break">
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
            {useMonthly
              ? `סיכום חודשי — יתרה מצטברת (${dayCount} ימים)`
              : `פירוט תנועות — יתרה מצטברת (${dayCount} ימים)`}
          </h2>

          {useMonthly ? (
            /*
             * ── MONTHLY SUMMARY TABLE (> 31 days) ────────────────────────────
             * Single source of truth: render directly from `chartData`, which is
             * the pre-bucketed, pre-balanced array produced by buildChartPoints()
             * in CashflowDashboard with mode='monthly'.  No re-aggregation here.
             *   · p.date    — Hebrew month label (e.g. "מרץ 2026")
             *   · p.income  — period income for that bucket (already correct)
             *   · p.expense — period expense (negative, already correct)
             *   · p.balance — running balance at END of bucket (already correct)
             */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#374151', fontWeight: 700, width: '28%' }}>חודש</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#065f46', fontWeight: 700, width: '24%' }}>הכנסות</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#991b1b', fontWeight: 700, width: '24%' }}>הוצאות</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#1e40af', fontWeight: 700, width: '24%' }}>יתרת תזרים</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((p, i) => (
                  <tr key={p.fullDate} className="print-tr-nobreak" style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9', color: '#374151', fontWeight: 600 }}>{p.date}</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9',
                      color: p.income  > 0 ? '#065f46' : '#d1d5db',
                      fontWeight: p.income  > 0 ? 600 : 400 }}>
                      {p.income  > 0 ? ILS.format(p.income)  : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9',
                      color: p.expense < 0 ? '#991b1b' : '#d1d5db',
                      fontWeight: p.expense < 0 ? 600 : 400 }}>
                      {p.expense < 0 ? ILS.format(p.expense) : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f1f5f9',
                      fontWeight: 700,
                      color: p.balance >= 0 ? '#065f46' : '#991b1b' }}>
                      {ILS.format(p.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Footer: total income, total expense, final running balance */}
              <tfoot>
                <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                  <td style={{ padding: '5px 8px', fontWeight: 700, color: '#374151', fontSize: 10 }}>סה״כ תקופה</td>
                  <td style={{ padding: '5px 8px', fontWeight: 700,
                    color: chartData.reduce((s, p) => s + p.income, 0) > 0 ? '#065f46' : '#d1d5db' }}>
                    {ILS.format(chartData.reduce((s, p) => s + p.income, 0))}
                  </td>
                  <td style={{ padding: '5px 8px', fontWeight: 700,
                    color: chartData.reduce((s, p) => s + p.expense, 0) < 0 ? '#991b1b' : '#d1d5db' }}>
                    {ILS.format(chartData.reduce((s, p) => s + p.expense, 0))}
                  </td>
                  <td style={{ padding: '5px 8px', fontWeight: 800,
                    color: (chartData[chartData.length - 1]?.balance ?? 0) >= 0 ? '#065f46' : '#991b1b' }}>
                    {ILS.format(chartData[chartData.length - 1]?.balance ?? 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            /* ── DETAIL TABLE (≤ 31 days) ── */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#374151', fontWeight: 700, width: '10%' }}>תאריך</th>
                  <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#374151', fontWeight: 700, width: '40%' }}>תיאור</th>
                  <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#065f46', fontWeight: 700, width: '17%' }}>הכנסות</th>
                  <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#991b1b', fontWeight: 700, width: '17%' }}>הוצאות</th>
                  <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', color: '#1e40af', fontWeight: 700, width: '16%' }}>יתרה</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((r, i) => (
                  <tr key={i} className="print-tr-nobreak" style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '3px 6px', borderBottom: '1px solid #f1f5f9', color: '#374151', fontFamily: 'monospace', fontSize: 8 }}>{r.date}</td>
                    <td style={{ padding: '3px 6px', borderBottom: '1px solid #f1f5f9', color: '#374151', maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={r.description}>{r.description}</td>
                    <td style={{ padding: '3px 6px', borderBottom: '1px solid #f1f5f9',
                      color: r.income  > 0 ? '#065f46' : '#d1d5db',
                      fontWeight: r.income  > 0 ? 600 : 400 }}>
                      {r.income  > 0 ? ILS.format(r.income)  : '—'}
                    </td>
                    <td style={{ padding: '3px 6px', borderBottom: '1px solid #f1f5f9',
                      color: r.expense < 0 ? '#991b1b' : '#d1d5db',
                      fontWeight: r.expense < 0 ? 600 : 400 }}>
                      {r.expense < 0 ? ILS.format(r.expense) : '—'}
                    </td>
                    <td style={{ padding: '3px 6px', borderBottom: '1px solid #f1f5f9',
                      fontWeight: 700,
                      color: r.balance >= 0 ? '#065f46' : '#991b1b' }}>
                      {ILS.format(r.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PAGE 2 — Cash-Flow Classification matrix                          */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div className="print-page-break" style={{ marginTop: 24 }}>
          <div style={{ borderBottom: '3px solid #10b981', paddingBottom: 8, marginBottom: 14 }}>
            <h1 style={{ fontSize: 15, fontWeight: 800, color: '#065f46', margin: 0 }}>
              סיכום לפי סיווג תזרים
            </h1>
            <p style={{ fontSize: 11, color: '#475569', margin: '3px 0 0' }}>
              חברת <strong>{companyName}</strong>
              &nbsp;·&nbsp;
              שנת {selectedYear}
            </p>
          </div>
          <PrintMatrix
            rows={cfMatrix.matrixRows}
            columnTotals={cfMatrix.columnTotals}
            grandTotal={cfMatrix.grandTotal}
            title="סיווג תזרים — חלוקה חודשית"
          />
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PAGE 3 — Accounting Classification matrix                         */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div className="print-page-break" style={{ marginTop: 24 }}>
          <div style={{ borderBottom: '3px solid #6366f1', paddingBottom: 8, marginBottom: 14 }}>
            <h1 style={{ fontSize: 15, fontWeight: 800, color: '#3730a3', margin: 0 }}>
              סיכום לפי סיווג הנהלת חשבונות
            </h1>
            <p style={{ fontSize: 11, color: '#475569', margin: '3px 0 0' }}>
              חברת <strong>{companyName}</strong>
              &nbsp;·&nbsp;
              שנת {selectedYear}
            </p>
          </div>
          <PrintMatrix
            rows={acctMatrix.matrixRows}
            columnTotals={acctMatrix.columnTotals}
            grandTotal={acctMatrix.grandTotal}
            title="סיווג הנהלת חשבונות — חלוקה חודשית"
          />
        </div>

      </div>
    );
  },
);
